import {
  reservationEventNames,
  purchaseEventNames,
  userAccountEventNames,
} from '../constants/events';
import serializePurchase from '../serializers/purchase';
import serializeReservation from '../serializers/reservation';
import serializeUser from '../serializers/user';
import { trackSignUp, trackLogIn } from '../ga.events';
import { debuggerMode } from '../debugger';
import { getTrackingLibrary } from '../trackingLibrary';
import { getSuperProperties } from './superProperties';

function logNamingError(name) {
  console.error('Analytics ➞ Event not sent, invalid event name:', name);
}

export function trackEvent(name, properties, options, callback) {
  // add the global super properties to all events
  const combinedProperties = { ...getSuperProperties(), ...properties };
  const trackingLibrary = getTrackingLibrary();

  if (trackingLibrary) {
    trackingLibrary.trackEvent(name, combinedProperties, options, callback);
  }

  if (debuggerMode()) {
    console.info(`Analytics ➞ Event sent: ${name}`, combinedProperties);
  }
}

/**
 * Send purchase related Analytics events:
 *  - Purchase Attempt
 *  - Passengers Created
 *  - Confirm Itinerary
 *
 * Purchase object required for these events:
 *
 * {
 *   token: 'j7yJCqO-wJ4',
 *   pricing: {
 *     total: 500,
 *     taxes: 40,
 *     markup: 25,
 *     insuranceAmount: 11.6,
 *     discountCode: 'BusSenda10',
 *   },
 *   international: false,
 *   isViajaAhorrando: false,
 *   paymentEngine: 'adyen', // `adyen` or `open_pay`
 *   purchaserNationality: 'México',
 *   passengerSelection: {
 *     adult: 2,
 *     child: 0,
 *     infant: 1
 *   },
 *   departs: {
 *     id: 'mont-gdlj-24mar170000-1093210-18', // trip slug
 *     origin: {
 *       id: 't-ciudad-de-mexico-central-norte',
 *       name: 'Central del Norte',
 *       city: {
 *         id: 'ciudad-de-mexico',
 *         name: 'Ciudad de México',
 *         state: {
 *           country: {
 *             name: 'México'
 *           }
 *         }
 *       }
 *     },
 *     destination: {
 *       // same as origin
 *     },
 *     departure: '2017-03-16T00:16:00',
 *     arrival: '2017-03-16T00:20:00',
 *     pricing: {
 *       amount: 150,
 *       taxes: 50,
 *       providerDiscount: 0,
 *       total: 200,
 *       commission: 10 // optional
 *     },
 *     tickets: [{
 *       category: 'general' // bus discount category
 *       total: 200
 *     }, {
 *       // more tickets
 *     }],
 *     fragments: [{
 *       transportType: 'flight', // flight | bus
 *       carrier: {
 *         name: 'Primera Plus',
 *         buslineGroup: { // Used for bus only
 *           name: 'Grupo Flecha Amarilla'
 *         },
 *         allowsSeatSelection: true,
 *         ally: true,
 *         copyrightProtected: false
 *       },
 *       origin: {
 *         id: 't-ciudad-de-mexico-central-norte',
 *         name: 'Central del Norte',
 *         city: {
 *           id: 'ciudad-de-mexico',
 *           name: 'Ciudad de México',
 *           state: {
 *             country: {
 *               name: 'México'
 *             }
 *           }
 *         }
 *       },
 *       destination: {
 *         // same as origin
 *       },
 *       departure: '2017-03-16T00:16:00',
 *       arrival: '2017-03-16T00:20:00',
 *       pricing: {
 *         total: 200,
 *         commission: 10
 *       },
 *       stops: 0,
 *       legs: [{ // Used for flight only
 *         carrier: {
 *           name: 'Interjet',
 *           allowsSeatSelection: true,
 *           ally: true,
 *           copyrightProtected: false
 *         },
 *         origin: {
 *           id: 't-ciudad-de-mexico-central-norte',
 *           name: 'Central del Norte',
 *           city: {
 *             id: 'ciudad-de-mexico',
 *             name: 'Ciudad de México',
 *             state: {
 *               country: {
 *                 name: 'México'
 *               }
 *             }
 *           }
 *         },
 *         destination: {
 *           // same as origin
 *         },
 *         departure: '2017-03-16T00:20:00',
 *         arrival: '2017-03-16T00:20:00'
 *       }]
 *     }, {
 *       // another mixed trip fragment (one way purchase)
 *     }]
 *   },
 *   returns: { // optional
 *     // same attrs as departs
 *   }
 * }
 */
export function trackPurchaseEvent(
  name,
  purchase,
  options,
  callback,
  extraProps
) {
  if (purchaseEventNames[name]) {
    trackEvent(
      name,
      { ...serializePurchase(purchase), ...extraProps },
      options,
      callback
    );
  } else if (debuggerMode()) {
    logNamingError(name);
  }
}

/**
 * Send reservation related Analytics events:
 *  - Seat Change
 *
 * Reservation object required for these events. This object is the same as
 * the departure and return properties of the purchase object:
 *
 * {
 *   id: 'mont-gdlj-24mar170000-1093210-18', // trip slug
 *   origin: {
 *     id: 't-ciudad-de-mexico-central-norte',
 *     name: 'Central del Norte',
 *     city: {
 *       id: 'ciudad-de-mexico',
 *       name: 'Ciudad de México',
 *       state: {
 *         country: {
 *           name: 'México'
 *         }
 *       }
 *     }
 *   },
 *   destination: {
 *     // same as origin
 *   },
 *   departure: '2017-03-16T00:16:00',
 *   arrival: '2017-03-16T00:20:00',
 *   pricing: {
 *     amount: 150,
 *     taxes: 50,
 *     providerDiscount: 0,
 *     total: 200,
 *     commission: 10 // optional
 *   },
 *   tickets: [{
 *     category: 'general' // bus discount category
 *     total: 200
 *   }, {
 *     // more tickets
 *   }],
 *   fragments: [{
 *     transportType: 'flight', // flight | bus
 *     carrier: {
 *       name: 'Primera Plus',
 *       buslineGroup: { // Used for bus only
 *         name: 'Grupo Flecha Amarilla'
 *       },
 *       allowsSeatSelection: true,
 *       ally: true,
 *       copyrightProtected: false
 *     },
 *     origin: {
 *       id: 't-ciudad-de-mexico-central-norte',
 *       name: 'Central del Norte',
 *       city: {
 *         id: 'ciudad-de-mexico',
 *         name: 'Ciudad de México',
 *         state: {
 *           country: {
 *             name: 'México'
 *           }
 *         }
 *       }
 *     },
 *     destination: {
 *       // same as origin
 *     },
 *     departure: '2017-03-16T00:16:00',
 *     arrival: '2017-03-16T00:20:00',
 *     pricing: {
 *       total: 200,
 *       commission: 10
 *     },
 *     stops: 0,
 *     legs: [{ // Used for flight only
 *       carrier: {
 *         name: 'Interjet',
 *         allowsSeatSelection: true,
 *         ally: true,
 *         copyrightProtected: false
 *       },
 *       origin: {
 *         id: 't-ciudad-de-mexico-central-norte',
 *         name: 'Central del Norte',
 *         city: {
 *           id: 'ciudad-de-mexico',
 *           name: 'Ciudad de México',
 *           state: {
 *             country: {
 *               name: 'México'
 *             }
 *           }
 *         }
 *       },
 *       destination: {
 *         // same as origin
 *       },
 *       departure: '2017-03-16T00:20:00',
 *       arrival: '2017-03-16T00:20:00'
 *     }]
 *   }, {
 *     // another mixed trip fragment (one way purchase)
 *   }]
 * }
 */
export function trackReservationEvent(
  name,
  reservation,
  options,
  callback,
  extraProps
) {
  if (reservationEventNames[name]) {
    trackEvent(
      name,
      { ...serializeReservation(reservation), ...extraProps },
      options,
      callback
    );
  } else if (debuggerMode()) {
    logNamingError(name);
  }
}

/**
 * Send user account related Analytics events:
 *  - Sign Up
 *  - Log In
 *  - Log Out
 *
 * User object required for these events:
 * ```
 * {
 *   loginType: 'google-oauth2',
 * }
 * ```
 */
export function trackUserAccountEvent(
  name,
  user,
  options,
  callback,
  extraProps
) {
  if (userAccountEventNames[name]) {
    trackEvent(
      name,
      { ...serializeUser(user), ...extraProps },
      options,
      callback
    );

    if (name === 'Sign Up') trackSignUp('Account', user.loginType);
    if (name === 'Log In') trackLogIn(user.loginType);
  } else if (debuggerMode()) {
    logNamingError(name);
  }
}



// WEBPACK FOOTER //
// ./events/core.js